import { useQuery } from "@tanstack/react-query";
import { workflowRunsService } from "@/services/workflowRunsService";
import { deriveNodeStatuses, isRunActive, type NodeRunInfo } from "./runStatusPure";

/**
 * Phase 4A.8 — poll a run's status + events while it's active and derive the
 * per-node status map for canvas decoration. Follows the WorkflowRunInspector
 * pattern (TanStack useQuery + conditional refetchInterval); polling stops
 * automatically once the run reaches a terminal state.
 */
export function useWorkflowRunStatus(runId: string | null) {
  const runQuery = useQuery({
    queryKey: ["wf-canvas-run", runId],
    enabled: !!runId,
    queryFn: () => workflowRunsService.getRun(runId!),
    refetchInterval: (q) => (isRunActive((q.state.data as { status?: string } | null)?.status) ? 1200 : false),
  });

  const runStatus = runQuery.data?.status;

  const eventsQuery = useQuery({
    queryKey: ["wf-canvas-run-events", runId],
    enabled: !!runId,
    queryFn: () => workflowRunsService.listEvents(runId!),
    refetchInterval: isRunActive(runStatus) ? 1200 : false,
  });

  const nodeStatuses: Record<string, NodeRunInfo> = eventsQuery.data
    ? deriveNodeStatuses(eventsQuery.data)
    : {};

  return {
    run: runQuery.data ?? null,
    runStatus: runStatus ?? null,
    active: isRunActive(runStatus),
    nodeStatuses,
    events: eventsQuery.data ?? [],
  };
}
